import { motion } from "framer-motion";
import { Target, TrendingUp, Users, Zap } from "lucide-react";

const CaseStudiesPage = () => {
  const caseStudies = [
    {
      icon: <Target size={32} />,
      industry: "D2C Fashion",
      title: "3x Revenue Growth Through Brand Positioning",
      challenge: "A D2C fashion brand struggled with low brand recall and high customer acquisition costs despite running multiple ad campaigns.",
      solution: "We developed a comprehensive brand communication strategy with clear positioning, storytelling frameworks, and AI-powered content workflows. Automated their email marketing and social media scheduling.",
      results: [
        "3.2x increase in monthly revenue within 6 months",
        "47% reduction in customer acquisition cost",
        "2.5x improvement in email open rates",
        "Built a community of 12K+ engaged followers"
      ],
      gradient: "from-brandPrimary/10 to-transparent"
    },
    {
      icon: <TrendingUp size={32} />,
      industry: "SaaS Startup",
      title: "Data-Driven Strategy for Market Entry",
      challenge: "A B2B SaaS startup entering the Indian market lacked clear positioning and struggled to identify their ideal customer profile.",
      solution: "Conducted AI-powered market research, competitor analysis, and audience segmentation. Built a complete go-to-market strategy with content pillars and lead generation automation.",
      results: [
        "Identified and validated 3 high-potential customer segments",
        "Generated 200+ qualified leads in first quarter",
        "Achieved 18% demo conversion rate",
        "Established thought leadership with 50+ published insights"
      ],
      gradient: "from-brandSecondary/10 to-transparent"
    },
    {
      icon: <Users size={32} />,
      industry: "MSME Manufacturing",
      title: "Digital Transformation for Traditional Business",
      challenge: "A 20-year-old manufacturing MSME wanted to transition from offline to digital but had zero online presence and limited marketing knowledge.",
      solution: "Developed foundational digital strategy including website, SEO, LinkedIn presence, and automated lead nurturing. Trained internal team on digital marketing basics.",
      results: [
        "Built digital presence from scratch to 5K+ monthly visitors",
        "Generated ₹2.5Cr+ in digital-sourced revenue",
        "Reduced sales cycle from 90 to 45 days",
        "Empowered internal team with marketing automation skills"
      ],
      gradient: "from-brandAccent/10 to-transparent"
    },
    {
      icon: <Zap size={32} />,
      industry: "Wellness D2C",
      title: "AI Automation for Scalable Growth",
      challenge: "A wellness D2C brand was overwhelmed with manual marketing tasks and couldn't scale content production to meet demand.",
      solution: "Implemented AI-powered content generation, automated social scheduling, customer segmentation, and personalized email workflows. Built custom dashboards for real-time performance tracking.",
      results: [
        "5x increase in content output with same team size",
        "Saved 20+ hours per week on manual tasks",
        "Improved customer retention by 34%",
        "Scaled from 2 to 7 marketing channels seamlessly"
      ],
      gradient: "from-brandPrimary/10 to-transparent"
    }
  ];

  return (
    <div className="min-h-screen noise-texture pt-32">
      {/* Hero Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-brandSecondary font-medium text-sm uppercase tracking-wider" data-testid="case-studies-label">
              Case Studies
            </span>
            <h1 className="font-heading font-bold text-5xl md:text-6xl text-white mt-4 mb-6" data-testid="case-studies-heading">
              Real Growth,
              <br />
              <span className="text-gradient">Real Results</span>
            </h1>
            <p className="text-gray-400 text-lg leading-relaxed" data-testid="case-studies-intro">
              Illustrative examples of how strategic clarity and AI-powered execution 
              drive measurable business outcomes.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto space-y-16">
          {caseStudies.map((study, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              data-testid={`case-study-${idx}`}
              className="glass-card rounded-3xl p-12 relative overflow-hidden"
            >
              {/* Gradient Background */}
              <div className={`absolute inset-0 bg-gradient-to-br ${study.gradient} pointer-events-none`}></div>
              
              <div className="relative z-10">
                {/* Header */}
                <div className="flex items-start gap-4 mb-8">
                  <div className="text-brandPrimary">
                    {study.icon}
                  </div>
                  <div>
                    <p className="text-brandSecondary font-medium text-sm mb-2">{study.industry}</p>
                    <h2 className="font-heading font-bold text-3xl md:text-4xl text-white">
                      {study.title}
                    </h2>
                  </div>
                </div>

                {/* Content */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                  {/* Challenge & Solution */}
                  <div className="lg:col-span-2 space-y-6">
                    <div>
                      <h3 className="font-heading font-semibold text-xl text-white mb-3">
                        The Challenge
                      </h3>
                      <p className="text-gray-400 leading-relaxed">
                        {study.challenge}
                      </p>
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-xl text-white mb-3">
                        Our Solution
                      </h3>
                      <p className="text-gray-400 leading-relaxed">
                        {study.solution}
                      </p>
                    </div>
                  </div>

                  {/* Results */}
                  <div className="glass-card rounded-2xl p-6 bg-white/5">
                    <h3 className="font-heading font-semibold text-xl text-white mb-4">
                      Key Results
                    </h3>
                    <ul className="space-y-3">
                      {study.results.map((result, rIdx) => (
                        <li key={rIdx} data-testid={`case-study-${idx}-result-${rIdx}`} className="text-gray-300 text-sm flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-brandSecondary flex-shrink-0 mt-2"></div>
                          <span>{result}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="glass-card rounded-2xl p-8 border-l-4 border-brandSecondary">
            <p className="text-gray-400 text-sm leading-relaxed" data-testid="disclaimer">
              <strong className="text-white">Note:</strong> These are illustrative examples based on typical 
              outcomes for businesses in similar situations. Actual results vary based on industry, market conditions, 
              budget, and execution. We focus on realistic, sustainable growth—not unrealistic promises.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudiesPage;